// Opt-in Netzwerk-Mess-Probe (Diagnose/Performance) — instrumentiert window.fetch,
// zählt Requests je Endpoint und erkennt Duplikat-Bursts (dieselbe URL mehrfach in
// kurzer Zeit = redundante Calls). Standardmäßig NO-OP: nur aktiv, wenn ausdrücklich
// eingeschaltet, damit Produktion/normaler Betrieb unberührt bleibt.
//
// AKTIVIEREN (eine Möglichkeit genügt):
//   localStorage.setItem('KMVS_PERF','1'); location.reload();
//   – oder – URL mit ?perf=1 aufrufen.
//
// AUSWERTEN in der DevTools-Konsole:
//   __perf.report()    → Tabelle: Endpoint × Anzahl / Ø-Dauer / Summe (nach Anzahl sortiert)
//   __perf.dupes(2000) → gleiche volle URL ≥2× innerhalb von 2000 ms (Redundanz-Kandidaten)
//   __perf.timeline()  → chronologische Liste aller erfassten Calls
//   __perf.reset()     → Zähler/Verlauf zurücksetzen (z. B. vor einem neuen Szenario)
//   __perf.auto(30)    → alle 30 s automatisch eine Kurz-Zusammenfassung loggen (0 = aus)
//   __perf.uninstall() → fetch-Patch entfernen
//
// DEAKTIVIEREN dauerhaft: localStorage.removeItem('KMVS_PERF'); location.reload();

export interface PerfCallRecord {
    /** HTTP-Methode. */
    method: string;
    /** Normalisierter Endpoint „METHOD /pfad" (ohne Query, numerische IDs → :id). */
    endpoint: string;
    /** Volle aufgerufene URL (inkl. Query) — für Duplikat-Erkennung. */
    url: string;
    /** Startzeit (performance.now(), ms). */
    startedAt: number;
    /** Dauer in ms. */
    durationMs: number;
    /** HTTP-Status oder "error". */
    status: number | "error";
}

interface PerfApi {
    report: () => void;
    dupes: (windowMs?: number) => void;
    timeline: () => PerfCallRecord[];
    records: () => PerfCallRecord[];
    reset: () => void;
    auto: (seconds: number) => void;
    uninstall: () => void;
}

const FLAG_KEY = "KMVS_PERF";

/** Ist die Probe eingeschaltet? (localStorage-Flag ODER ?perf=1). */
function isEnabled(): boolean {
    if (typeof window === "undefined") return false;
    try {
        if (window.localStorage.getItem(FLAG_KEY) === "1") return true;
    } catch {
        /* localStorage evtl. blockiert */
    }
    return /[?&]perf=1\b/.test(window.location.search);
}

/** Pfad normalisieren: Query weg, numerische / UUID-Segmente → :id, /api-Prefix weg. */
function normalizePath(pathname: string): string {
    return pathname
        .replace(/^\/api\//, "/")
        .split("/")
        .map((seg) =>
            /^\d+$/.test(seg) || /^[0-9a-f]{8}-[0-9a-f-]{20,}$/i.test(seg) ? ":id" : seg,
        )
        .join("/");
}

function toUrl(input: RequestInfo | URL): string {
    if (typeof input === "string") return input;
    if (input instanceof URL) return input.toString();
    // Request
    return input.url;
}

function methodOf(input: RequestInfo | URL, init?: RequestInit): string {
    if (init?.method) return init.method.toUpperCase();
    if (typeof input === "object" && input !== null && "method" in input && input.method) {
        return String(input.method).toUpperCase();
    }
    return "GET";
}

let installed = false;

/**
 * Installiert die Netzwerk-Probe, WENN sie eingeschaltet ist — sonst No-op.
 * Idempotent (mehrfacher Aufruf schadet nicht). Am App-Entry aufrufen.
 */
export function installPerfProbe(): void {
    if (typeof window === "undefined" || installed || !isEnabled()) return;
    installed = true;

    const records: PerfCallRecord[] = [];
    const origFetch = window.fetch.bind(window);
    let autoTimer: number | null = null;
    let lastAutoCount = 0;

    const patched: typeof window.fetch = async (input, init) => {
        const method = methodOf(input, init);
        const url = toUrl(input);
        let endpoint = `${method} ${url}`;
        try {
            const u = new URL(url, window.location.origin);
            endpoint = `${method} ${normalizePath(u.pathname)}`;
        } catch {
            /* nicht-parsebare URL → volle URL als Endpoint */
        }
        const startedAt = performance.now();
        try {
            const res = await origFetch(input as RequestInfo, init);
            records.push({ method, endpoint, url, startedAt, durationMs: performance.now() - startedAt, status: res.status });
            return res;
        } catch (err) {
            records.push({ method, endpoint, url, startedAt, durationMs: performance.now() - startedAt, status: "error" });
            throw err;
        }
    };
    window.fetch = patched;

    const summarize = () => {
        const byEndpoint = new Map<string, { count: number; totalMs: number }>();
        for (const r of records) {
            const e = byEndpoint.get(r.endpoint) ?? { count: 0, totalMs: 0 };
            e.count += 1;
            e.totalMs += r.durationMs;
            byEndpoint.set(r.endpoint, e);
        }
        return Array.from(byEndpoint.entries())
            .map(([endpoint, v]) => ({ endpoint, count: v.count, avgMs: Math.round(v.totalMs / v.count), totalMs: Math.round(v.totalMs) }))
            .sort((a, b) => b.count - a.count);
    };

    const api: PerfApi = {
        report: () => {
            const rows = summarize();
            const total = records.length;
            // eslint-disable-next-line no-console
            console.log(`%c[KMVS-Perf] ${total} Requests · ${rows.length} verschiedene Endpoints`, "font-weight:bold");
            // eslint-disable-next-line no-console
            console.table(rows);
        },
        dupes: (windowMs = 2000) => {
            const byUrl = new Map<string, number[]>();
            for (const r of records) {
                const arr = byUrl.get(r.url) ?? [];
                arr.push(r.startedAt);
                byUrl.set(r.url, arr);
            }
            const out: { url: string; count: number; innerhalbMs: number }[] = [];
            for (const [url, times] of byUrl) {
                times.sort((a, b) => a - b);
                for (let i = 1; i < times.length; i += 1) {
                    if (times[i] - times[i - 1] <= windowMs) {
                        out.push({ url, count: times.length, innerhalbMs: Math.round(times[times.length - 1] - times[0]) });
                        break;
                    }
                }
            }
            // eslint-disable-next-line no-console
            console.log(`%c[KMVS-Perf] Duplikat-Bursts (gleiche URL ≥2× in ${windowMs} ms):`, "font-weight:bold");
            // eslint-disable-next-line no-console
            console.table(out.sort((a, b) => b.count - a.count));
        },
        timeline: () => records.slice(),
        records: () => records.slice(),
        reset: () => {
            records.length = 0;
            lastAutoCount = 0;
            // eslint-disable-next-line no-console
            console.log("[KMVS-Perf] zurückgesetzt.");
        },
        auto: (seconds: number) => {
            if (autoTimer !== null) {
                window.clearInterval(autoTimer);
                autoTimer = null;
            }
            if (seconds > 0) {
                autoTimer = window.setInterval(() => {
                    if (records.length === lastAutoCount) return;
                    lastAutoCount = records.length;
                    api.report();
                }, seconds * 1000);
            }
        },
        uninstall: () => {
            window.fetch = origFetch;
            if (autoTimer !== null) window.clearInterval(autoTimer);
            installed = false;
            // eslint-disable-next-line no-console
            console.log("[KMVS-Perf] fetch-Patch entfernt.");
        },
    };

    (window as unknown as { __perf: PerfApi }).__perf = api;
    // eslint-disable-next-line no-console
    console.log(
        "%c[KMVS-Perf] aktiv — Auswertung: __perf.report() · __perf.dupes() · __perf.reset() · __perf.uninstall()",
        "color:#7d2169;font-weight:bold",
    );
}
